import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, Sparkles, MessageCircle, Truck } from "lucide-react";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import { useSettings } from "../context/SettingsContext";

const HERO_FALLBACK = {
  title: "Everything for your home, from one trusted store",
  subtitle:
    "Browse our range, add to cart, and confirm your order instantly on WhatsApp.",
  image: "https://images.unsplash.com/photo-1621593445639-bc924f9743b6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};

export default function Home() {
  const [products, setProducts] = useState([]);
  const { settings, categories, banners, categoryTree } = useSettings();

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data.slice(0, 4))).catch(() => {});
  }, []);

  const storeName = settings.store_name || "Victory General Store";
  const heroTitle = settings.hero_title || HERO_FALLBACK.title;
  const heroSubtitle = settings.hero_subtitle || HERO_FALLBACK.subtitle;
  const heroImage = settings.hero_image_url || HERO_FALLBACK.image;

  return (
    <div>
      {/* Hero */}
      {settings.hero_enabled && (
        <section className="relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center py-16 sm:py-24">
            <div className="animate-fade-up">
              <span className="inline-flex items-center gap-2 bg-[#F4EFE6] text-[var(--brand-primary)] text-sm font-medium px-3 py-1 rounded-full">
                <Sparkles className="w-4 h-4" strokeWidth={1.5} /> {storeName}
              </span>
              <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#1A231C] font-display">
                {heroTitle}
              </h1>
              <p className="mt-5 text-base sm:text-lg text-[#5C665D] leading-relaxed max-w-lg">
                {heroSubtitle}
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link
                  to="/shop"
                  data-testid="hero-shop-button"
                  className="inline-flex items-center gap-2 bg-[var(--brand-primary)] text-white rounded-lg px-6 py-3 font-medium hover:bg-[var(--brand-primary-hover)] transition-colors"
                >
                  Start Shopping <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
            <div className="animate-fade-up">
              <div className="rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(43,91,58,0.15)]">
                <img src={heroImage} alt={storeName} className="w-full h-[420px] object-cover" />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 pt-8">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {[
            { icon: Truck, title: "Fresh & Quality", desc: "Handpicked products you can trust." },
            { icon: MessageCircle, title: "Order on WhatsApp", desc: "One tap to confirm your order with us." },
            { icon: Sparkles, title: "Wide Range", desc: "Everything for your home in one place." },
          ].map((f) => (
            <div key={f.title} className="bg-white border border-[#E8E4D9] rounded-xl p-6">
              <span className="w-11 h-11 rounded-lg bg-[#F4EFE6] flex items-center justify-center mb-4">
                <f.icon className="w-5 h-5 text-[var(--brand-primary)]" strokeWidth={1.5} />
              </span>
              <h3 className="font-display font-medium text-lg text-[#1A231C]">{f.title}</h3>
              <p className="text-sm text-[#5C665D] mt-1">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Promo banner */}
      {settings.promo_enabled && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Link
            to={settings.promo_link || "/shop"}
            data-testid="promo-banner"
            className="group relative block rounded-2xl overflow-hidden h-56 sm:h-64"
          >
            {settings.promo_image_url && (
              <img
                src={settings.promo_image_url}
                alt={settings.promo_title || "Promotion"}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/20" />
            <div className="absolute inset-0 flex flex-col justify-center px-8 sm:px-12 max-w-xl">
              <h2 className="text-white text-2xl sm:text-3xl font-display font-bold">
                {settings.promo_title || "Special Offer"}
              </h2>
              {settings.promo_subtitle && (
                <p className="text-white/85 mt-2">{settings.promo_subtitle}</p>
              )}
              <span className="mt-4 inline-flex items-center gap-1 text-white font-medium">
                Shop now <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </Link>
        </section>
      )}

      {/* Promo tiles (banners) */}
      {banners.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className={`grid gap-6 ${banners.length === 1 ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2"}`}>
            {banners.map((b) => {
              const isExternal = /^https?:\/\//i.test(b.link || "");
              const tileInner = (
                <>
                  {b.image_url && (
                    <img src={b.image_url} alt={b.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/10" />
                  <div className="absolute inset-0 flex flex-col justify-center px-6 sm:px-8">
                    {b.title && <h3 className="text-white text-xl sm:text-2xl font-display font-bold">{b.title}</h3>}
                    {b.subtitle && <p className="text-white/85 mt-1 text-sm">{b.subtitle}</p>}
                  </div>
                </>
              );
              const cls = "group relative block rounded-2xl overflow-hidden h-48 sm:h-56";
              return isExternal ? (
                <a key={b.id} href={b.link} target="_blank" rel="noreferrer" data-testid={`promo-tile-${b.id}`} className={cls}>
                  {tileInner}
                </a>
              ) : (
                <Link key={b.id} to={b.link || "/shop"} data-testid={`promo-tile-${b.id}`} className={cls}>
                  {tileInner}
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Categories */}
      {settings.show_category_grid && categoryTree.length > 0 && (        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#1A231C] font-display mb-8">
            Shop by category
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {categoryTree.map((c) => (
              <Link
                key={c.key}
                to={`/shop?category=${c.key}`}
                data-testid={`category-${c.key}`}
                className="group relative rounded-2xl overflow-hidden h-52"
              >
                <img
                  src={c.image_url}
                  alt={c.label}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-white text-lg font-display font-semibold leading-tight">{c.label}</h3>
                  <span className="text-white/80 text-sm inline-flex items-center gap-1 mt-0.5">
                    Explore <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Featured products */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 pb-20">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#1A231C] font-display">
            Popular right now
          </h2>
          <Link to="/shop" className="text-[var(--brand-primary)] font-medium inline-flex items-center gap-1 hover:gap-2 transition-all">
            View all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {products.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      </section>
    </div>
  );
}
